import {
  globalMessageHandler,
  GridModalFormProps,
  useForm,
  UseFormReturnType,
  Validator,
} from "common";
import { useParams } from "common/components/Form/ParamsContext";
import { BoatItem } from "features/boats/boatApi";
import { DictionaryItem } from "features/dictionaries/dictionaryApi";
import useDictionaries from "features/dictionaries/useDictionaries";
import I18n from "i18n";
import { useMemo } from "react";
import { Params } from "react-router-dom";
import { departureCapacityRules } from "./departureCapacityRules";
import {
  EventGroupItem,
  EventGroupOptions,
  getEventGroupService,
  getGroupEventGroupService,
} from "./eventGroupApi";

export interface UseEventGroupFormProps extends GridModalFormProps {
  eventGroupIds?: number[];
}

export interface UseEventGroupFormReturnType
  extends UseFormReturnType<EventGroupItem, EventGroupOptions> {
  params?: Readonly<Params<"event_group_id">>;
  selectedBoat?: DictionaryItem<BoatItem>;
  groupEdit?: boolean;
}

export default function useEventGroupForm({
  id,
  afterSave,
  defaultValues,
  eventGroupIds,
}: UseEventGroupFormProps): UseEventGroupFormReturnType {
  const params = useParams<"event_group_id">();
  const scope = undefined;
  const groupEdit = !!eventGroupIds;
  const service = groupEdit
    ? getGroupEventGroupService(scope, eventGroupIds)
    : getEventGroupService(scope);
  const { dictionaries, resolveOptions, filterOptions, resolveOption } =
    useDictionaries();

  const form = useForm<EventGroupItem, EventGroupOptions>({
    id: params.event_group_id || id,
    ignoreNavigateAfterCreate: true,
    translationCategory: "EventGroup",
    rules: {
      boat_id: Validator.rule.any().required(),
      start_time: Validator.rule.any().required(),
      duration: Validator.rule.any().required(),
      ...departureCapacityRules(!groupEdit),
    },
    afterSave,
    defaultValues,
    onValidationFailed: () => {
      globalMessageHandler.snack({
        severity: "error",
        message: I18n.t("App.formHasErrors"),
      });
    },
    ...service,
    loadOptions: undefined,
  });

  const options = useMemo(() => {
    return {
      boat_id: dictionaries.boats,
    };
  }, [dictionaries?.boats]);
  const selectedBoat = useMemo(() => {
    return resolveOption("boats", form.item?.boat_id);
  }, [dictionaries?.boats, form.item?.boat_id]);

  return { ...form, options, params, selectedBoat, groupEdit };
}
